// USER-EDITABLE NAMEPLATE PALETTE
//
// Every colour in the board's Color Legend can be clicked and changed. Choices live in localStorage
// on this machine only -- they are never sent anywhere and never persisted to the backend.
//
// WHY A MODULE STORE RATHER THAN A REACT CONTEXT: NAMEPLATE_COLORS is read by pure, non-component
// functions in staffNameplates.ts (crnaNameplateColor, doctorNameplateColor, studentNameplateColor).
// Those cannot call hooks, so a context would force them to become hooks and ripple through every
// caller. A module store keeps them pure.
//
// READS STAY `NAMEPLATE_COLORS.docSolo` EVERYWHERE: that export is a Proxy over the defaults that
// consults the overrides on each property read, so the existing call sites did not have to change.
// Two consequences that WILL bite if ignored:
//   1. A module-level CAPTURE freezes the value at import time:
//        const SOLO = NAMEPLATE_COLORS.docSolo;   // <-- never sees a later edit
//      Read through the object at the point of use instead.
//   2. `export const NERVE_BLOCK_BORDER = '#f2c94c'` is frozen for the same reason, which is why the
//      two case-border colours live IN this palette and their consumers read
//      NAMEPLATE_COLORS.nerveBlockBorder / .pediatricBorder.

export const PALETTE_STORAGE_KEY = 'oc-nameplate-palette';
/** Fired on window after any palette write. `storage` only fires in OTHER tabs, so same-tab edits need this. */
export const PALETTE_CHANGED_EVENT = 'oc-palette-changed';

// The shipped palette. Solid fills, white plate text (RoomNamePlates hard-codes #fff), so a colour
// chosen here wants to stay dark enough to read white on.
export const PALETTE_DEFAULTS = {
  // CRNAs — by shift end (green family):
  crnaOut3pm: '#34c38f',
  crnaOut5pm: '#12795a',
  crnaOut7pm: '#9ac53a',
  crnaOvernight: '#6b7785',
  crnaDefault: '#7a8a99',

  // Anesthesiologists — blue family, with two accents:
  docSupervise: '#4a9fd4',
  docSolo: '#c42d86',
  // Shift-worker doctors by shift end, mirroring the CRNA family. docOut3pm keeps the old
  // docReliefNeeded gold so today's 3pm plates look unchanged; 5pm and 7pm darken from there and stay
  // clear of the supervise blue and the solo fuchsia.
  docOut3pm: '#b8860b',
  docOut5pm: '#c2621e',
  docOut7pm: '#8c3a0f',
  // LEGACY, no longer offered in the legend: the single "out 3pm/5pm, needs relief" alarm that the
  // three buckets above replaced. Kept in the palette so a plate someone manually recoloured with it
  // still resolves instead of being orphaned (same reason PALETTE_LEGACY_VALUES exists).
  docReliefNeeded: '#b8860b',
  docDefault: '#1a7abf',

  // Students — by competency:
  studentNew: '#d92d20',
  studentIntermediate: '#d9a406',
  studentGreen: '#1f9d55',
  studentDefault: '#7a8a99',

  request: '#a020f0',
  multiStart: '#e07a7a',

  // Pecking order — its own colours per role. These sections previously borrowed docSupervise (the
  // "supervising CRNA(s)" blue), which said the wrong thing; pecking order is a ranking, not a pairing.
  peckingDoctor: '#3f51b5',
  peckingCrna: '#0e8f8f',

  // Case borders (hollow outlines on the case blocks, not plate fills):
  nerveBlockBorder: '#f2c94c',
  pediatricBorder: '#ff2d9b',

  text: '#ffffff',
};

export type PaletteKey = keyof typeof PALETTE_DEFAULTS;

/**
 * Hexes a category USED to have.
 *
 * This matters because two of these colours are SEMANTIC MARKERS, not just colours: a per-allocation
 * `colorOverrides` entry stores a literal hex, and the board compares it against the palette to mean
 * "this doctor is marked solo" / "marked request". Recolouring therefore orphans overrides saved
 * under the previous value -- which already happened when docSolo went burnt orange -> fuchsia and
 * multiStart vivid purple -> faded red on 2026-10-03. matchesPaletteColor accepts these so saved
 * overrides keep working across a recolour.
 */
export const PALETTE_LEGACY_VALUES: Partial<Record<PaletteKey, string[]>> = {
  docSolo: ['#c25d1e'],
  multiStart: ['#9333ea'],
};

/** The categories the Color Legend exposes for editing, in the order it shows them. */
export const PALETTE_GROUPS: { title: string; kind: 'fill' | 'border'; keys: PaletteKey[] }[] = [
  { title: 'CRNA — by shift end', kind: 'fill', keys: ['crnaOut3pm', 'crnaOut5pm', 'crnaOut7pm', 'crnaOvernight'] },
  { title: 'Anesthesiologist', kind: 'fill', keys: ['docSupervise', 'docSolo', 'docOut3pm', 'docOut5pm', 'docOut7pm'] },
  { title: 'Student', kind: 'fill', keys: ['studentGreen', 'studentIntermediate', 'studentNew'] },
  { title: 'Pecking order', kind: 'fill', keys: ['peckingDoctor', 'peckingCrna'] },
  { title: 'Other', kind: 'fill', keys: ['multiStart'] },
  // `request` sits with the other border cues: it draws a ring over the plate rather than replacing
  // its fill, the same way the nerve-block and pediatric borders draw over a case block.
  { title: 'Case border', kind: 'border', keys: ['nerveBlockBorder', 'pediatricBorder', 'request'] },
];

/**
 * Categories drawn as a hollow BORDER swatch in the legend rather than a solid fill, because that is
 * how they render on the board: an outline over the existing colour instead of replacing it.
 *
 * `request` is here even though it sits in the "Other" group, so kind is resolved PER KEY -- its group
 * mate `multiStart` is still a fill.
 */
export const BORDER_PALETTE_KEYS: ReadonlySet<PaletteKey> = new Set<PaletteKey>([
  'nerveBlockBorder', 'pediatricBorder', 'request',
]);

export const PALETTE_LABELS: Partial<Record<PaletteKey, string>> = {
  crnaOut3pm: 'Out 3pm',
  crnaOut5pm: 'Out 5pm',
  crnaOut7pm: 'Out 7pm / 3–11pm',
  crnaOvernight: '11pm–7am',
  docSupervise: 'Supervising CRNA(s)',
  docSolo: 'Solo in a room',
  docOut3pm: 'Out 3pm',
  docOut5pm: 'Out 5pm',
  docOut7pm: 'Out 7pm',
  docReliefNeeded: 'Out 3pm/5pm',
  studentGreen: 'Senior',
  studentIntermediate: 'Intermediate',
  studentNew: 'Junior',
  peckingDoctor: 'Anesthesiologists',
  peckingCrna: 'CRNAs',
  request: 'Request case',
  multiStart: 'Multiple rooms',
  nerveBlockBorder: 'Nerve block',
  pediatricBorder: 'Pediatric',
};

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const isKey = (k: string): k is PaletteKey => Object.prototype.hasOwnProperty.call(PALETTE_DEFAULTS, k);

export function loadPaletteOverrides(): Partial<Record<PaletteKey, string>> {
  try {
    const raw = localStorage.getItem(PALETTE_STORAGE_KEY);
    if (!raw) return {};
    const v = JSON.parse(raw);
    // Accept only a plain object of key -> #rrggbb. A corrupt or hand-edited entry falls back to the
    // defaults rather than poisoning every nameplate on the board.
    if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
    const out: Partial<Record<PaletteKey, string>> = {};
    for (const k of Object.keys(v)) {
      const val = v[k];
      if (isKey(k) && typeof val === 'string' && HEX_RE.test(val)) out[k] = val;
    }
    return out;
  } catch { return {}; }
}

let overrides: Partial<Record<PaletteKey, string>> = loadPaletteOverrides();

function persist(): void {
  try {
    if (Object.keys(overrides).length === 0) localStorage.removeItem(PALETTE_STORAGE_KEY);
    else localStorage.setItem(PALETTE_STORAGE_KEY, JSON.stringify(overrides));
  } catch { /* storage unavailable (private mode) -- keep the in-memory edit */ }
  try { window.dispatchEvent(new CustomEvent(PALETTE_CHANGED_EVENT)); } catch { /* no window (tests) */ }
}

/** The colour in force for a category: the user's override if any, else the shipped default. */
export const paletteColor = (key: PaletteKey): string => overrides[key] ?? PALETTE_DEFAULTS[key];

/** Set one category's colour. Ignores an unknown key or a non-#rrggbb value; returns whether it took. */
export function setPaletteColor(key: string, hex: string): boolean {
  if (!isKey(key) || typeof hex !== 'string' || !HEX_RE.test(hex)) return false;
  if (hex.toLowerCase() === PALETTE_DEFAULTS[key].toLowerCase()) delete overrides[key];
  else overrides[key] = hex;
  persist();
  return true;
}

export function resetPaletteColor(key: string): void {
  if (!isKey(key)) return;
  delete overrides[key];
  persist();
}

export function resetPalette(): void { overrides = {}; persist(); }

export const isPaletteOverridden = (key: PaletteKey): boolean => overrides[key] !== undefined;
export const hasPaletteOverrides = (): boolean => Object.keys(overrides).length > 0;

/** Re-read storage (another tab changed it). */
export function reloadPaletteOverrides(): void { overrides = loadPaletteOverrides(); }

/**
 * Does `hex` mean this category? Accepts the colour in force, the shipped default, AND any legacy
 * value -- see PALETTE_LEGACY_VALUES. Use this instead of `=== NAMEPLATE_COLORS.x` wherever a STORED
 * hex is being read as a semantic marker, or recolouring silently orphans saved overrides.
 */
export function matchesPaletteColor(key: PaletteKey, hex?: string | null): boolean {
  if (!hex) return false;
  const h = hex.toLowerCase();
  if (h === paletteColor(key).toLowerCase()) return true;
  if (h === PALETTE_DEFAULTS[key].toLowerCase()) return true;
  return (PALETTE_LEGACY_VALUES[key] || []).some(v => v.toLowerCase() === h);
}

/**
 * The palette as a plain object. A Proxy so `NAMEPLATE_COLORS.docSolo` resolves the CURRENT colour on
 * every read, which is what let the existing call sites stay untouched. ownKeys/descriptor traps are
 * present so Object.entries and spread still enumerate it.
 */
export const NAMEPLATE_COLORS: Record<PaletteKey, string> = new Proxy(
  {} as Record<PaletteKey, string>,
  {
    get: (_t, prop) => (typeof prop === 'string' && isKey(prop) ? paletteColor(prop) : undefined),
    has: (_t, prop) => typeof prop === 'string' && isKey(prop),
    ownKeys: () => Object.keys(PALETTE_DEFAULTS),
    getOwnPropertyDescriptor: (_t, prop) =>
      typeof prop === 'string' && isKey(prop)
        ? { value: paletteColor(prop), enumerable: true, configurable: true, writable: false }
        : undefined,
    set: () => false,      // edits go through setPaletteColor so they persist and notify
    deleteProperty: () => false,
  },
);
